import * as React from "react"
import { Input } from "@nextui-org/react"
import { ChevronDownIcon } from "lucide-react"

import { IconLoader } from "@/containers/Loaders"
import { CustomerStaffModel } from "@/features/customers/models"
import { useAutocomplete } from "@/hooks/useAutocomplete"
import { cn } from "@/utils/styles"

type CustomerStaffsAutocompleteProps = {
  value: string
  staffs: CustomerStaffModel[]
  onSelect: (staff: CustomerStaffModel | null) => void
  onClear: () => void
  onChangeInput?: (value: string) => void
  placeholder?: string
  error?: string
  isLoading?: boolean
  isInvalid?: boolean
  disabled?: boolean
}

export const CustomerStaffsAutocomplete = React.forwardRef<
  HTMLInputElement,
  CustomerStaffsAutocompleteProps
>(
  (
    {
      value,
      staffs,
      onSelect,
      onClear,
      onChangeInput,
      placeholder,
      error,
      isLoading,
      isInvalid,
      disabled,
    },
    ref,
  ) => {
    const scrollAreaRef = React.useRef<HTMLDivElement>(null)
    const inputRef = React.useRef<HTMLInputElement>(null)

    const onSearch = (value: string) => {
      onChangeInput?.(value)
      if (value === "") {
        return staffs
      }

      const searched = staffs.filter(staff => {
        return (
          staff.email?.toLowerCase().includes(value.toLowerCase()) ||
          staff.displayName?.toLowerCase().includes(value.toLowerCase())
        )
      })
      return searched
    }

    const identify = (item: CustomerStaffModel) => {
      return item.email || item.displayName || ""
    }

    const {
      searchText,
      searchedItems,
      activeKey,
      isOpened,
      setOpened,
      handleValueChange,
      handleSelectItem,
      handleKeyDown,
      handleFocus,
    } = useAutocomplete({
      query: value,
      items: staffs,
      identify,
      onSearch,
      onSelect,
      onClear,
      scrollAreaRef,
    })

    return (
      <div className="relative" ref={ref}>
        <Input
          ref={inputRef}
          value={searchText}
          isInvalid={isInvalid}
          errorMessage={error}
          autoComplete="off"
          size="lg"
          placeholder={placeholder}
          onValueChange={handleValueChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          labelPlacement="outside"
          variant="bordered"
          className="bolta-input"
          isDisabled={disabled}
          endContent={
            <div className="text-gray-10">
              {isLoading ? (
                <IconLoader className="size-3" />
              ) : (
                <ChevronDownIcon
                  className={cn(
                    "size-4",
                    "text-[#878E95]",
                    isOpened ? "animate-rotate-down" : "animate-rotate-up",
                  )}
                />
              )}
            </div>
          }
        />

        {isOpened && (
          <div className="relative z-[23]">
            <div
              className="fixed inset-0 z-[23] h-full w-full"
              onClick={() => {
                setOpened(false)
              }}
            />

            <div className="absolute z-[23] mt-1 w-full rounded-lg border bg-white px-2 pb-2 pt-1 text-[15px] shadow-lg">
              <div
                className="customer-wrapper max-h-[188px] overflow-y-auto"
                ref={scrollAreaRef}
              >
                {staffs.length === 0 && (
                  <div className="px-3 pb-1 pt-2 text-gray-06">
                    등록된 고객이 없습니니다.
                  </div>
                )}
                {searchedItems.map((item, index) => {
                  const isActive = activeKey === index
                  return (
                    <div
                      key={identify(item)}
                      className={cn(
                        "flex items-center",
                        "my-1 cursor-pointer rounded px-3 py-[6px] text-gray-10 hover:bg-[#F6F8FB]",
                        {
                          "bg-[#F6F8FB]": isActive,
                        },
                      )}
                      onClick={() => {
                        handleSelectItem(item)
                      }}
                    >
                      <span>{item.email}</span>
                      {item.displayName && (
                        <span className="ml-2 text-gray-08">
                          ({item.displayName})
                        </span>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    )
  },
)
CustomerStaffsAutocomplete.displayName = "CustomerStaffsAutocomplete"
